import { useMemo, useState, useEffect } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { CATEGORY_INFO, orderGroups } from '@/lib/utils/toolGroups'
import type { CatalogTool } from '@/lib/api/toolSelection'

/** Collapse whitespace and keep at most `maxWords` words (append … if cut). */
function summarize(text: string, maxWords = 9): string {
  const clean = (text ?? '').replace(/\s+/g, ' ').trim()
  if (!clean) return ''
  const words = clean.split(' ')
  return words.length <= maxWords ? clean : words.slice(0, maxWords).join(' ') + '…'
}

interface ToolSelectorDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  catalog: CatalogTool[]
  /** Currently enabled tool names. */
  value: string[]
  /** Save handler — receives the full desired set (editable) or is called with the union (addOnly). */
  onSave: (enabledTools: string[]) => void
  /** addOnly: enabled tools cannot be turned off (mid-conversation). */
  addOnly?: boolean
  /** coreLocked: Core group cannot be turned off (Tools tab default template). */
  coreLocked?: boolean
  title?: string
}

export function ToolSelectorDialog({
  open,
  onOpenChange,
  catalog,
  value,
  onSave,
  addOnly = false,
  coreLocked = false,
  title = 'Select tools',
}: ToolSelectorDialogProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set(value))
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['core']))

  // Re-sync when the dialog is (re)opened with new value.
  useEffect(() => {
    if (open) setSelected(new Set(value))
  }, [open, value])

  const initiallyOn = useMemo(() => new Set(value), [value])

  const byGroup = useMemo(() => {
    const m = new Map<string, CatalogTool[]>()
    for (const t of catalog) {
      const arr = m.get(t.group) ?? []
      arr.push(t)
      m.set(t.group, arr)
    }
    return m
  }, [catalog])

  const groups = useMemo(() => orderGroups(byGroup.keys()), [byGroup])

  const isLocked = (group: string, name: string) =>
    (addOnly && initiallyOn.has(name)) || (coreLocked && group === 'core')

  const toggleTool = (group: string, name: string) => {
    if (isLocked(group, name)) return
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  const toggleSection = (group: string, tools: CatalogTool[]) => {
    setSelected((prev) => {
      const next = new Set(prev)
      const allOn = tools.every((t) => next.has(t.name))
      for (const t of tools) {
        if (allOn) {
          // turning a section off — respect locks
          if (!isLocked(group, t.name)) next.delete(t.name)
        } else {
          next.add(t.name)
        }
      }
      return next
    })
  }

  const handleSave = () => {
    onSave(Array.from(selected))
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {addOnly && (
          <p className="text-xs text-muted-foreground">
            Tools can be added mid-conversation but not removed.
          </p>
        )}
        <div className="space-y-2 min-w-0">
          {groups.map((group) => {
            const tools = byGroup.get(group) ?? []
            const info = (CATEGORY_INFO as Record<string, { title: string }>)[group]
            const isOpen = expanded.has(group)
            const allOn = tools.every((t) => selected.has(t.name))
            return (
              <div key={group} className="border rounded-md">
                <div className="flex items-center justify-between p-2">
                  <button
                    type="button"
                    className="flex items-center gap-1 text-sm font-medium"
                    onClick={() =>
                      setExpanded((prev) => {
                        const n = new Set(prev)
                        if (n.has(group)) n.delete(group)
                        else n.add(group)
                        return n
                      })
                    }
                  >
                    {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    {info?.title ?? group}
                  </button>
                  <Switch
                    checked={allOn}
                    onCheckedChange={() => toggleSection(group, tools)}
                    aria-label={`Toggle ${info?.title ?? group}`}
                  />
                </div>
                {isOpen && (
                  <div className="px-3 pb-2 space-y-0.5">
                    {tools.map((t) => {
                      const full = (t.description ?? '').replace(/\s+/g, ' ').trim()
                      const short = summarize(full)
                      const truncated = !!full && short !== full
                      // Single compact line: tool name (never cut) + a short word-based
                      // summary that fills the rest. Full text lives in the tooltip.
                      const label = (
                        <div
                          className={`flex min-w-0 flex-1 items-baseline gap-2 ${truncated ? 'cursor-help' : ''}`}
                          tabIndex={truncated ? 0 : undefined}
                        >
                          <span className="shrink-0 text-xs font-mono">{t.name}</span>
                          {short && (
                            <span className="truncate text-[11px] text-muted-foreground">{short}</span>
                          )}
                        </div>
                      )
                      return (
                        <div key={t.name} className="flex items-center gap-2 min-w-0 py-0.5">
                          {truncated ? (
                            <Tooltip>
                              <TooltipTrigger asChild>{label}</TooltipTrigger>
                              <TooltipContent
                                side="left"
                                className="max-w-xs whitespace-normal break-words text-xs leading-snug"
                              >
                                {full}
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            label
                          )}
                          <Switch
                            className="shrink-0"
                            checked={selected.has(t.name)}
                            disabled={isLocked(group, t.name)}
                            onCheckedChange={() => toggleTool(group, t.name)}
                            aria-label={`Toggle ${t.name}`}
                          />
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
